import { ReactElement, useCallback, useMemo, useState } from 'react'
import isEqual from 'lodash-es/isEqual'
import { Stack } from '@mui/material'
import type { ShearConnectionDTO, ShearConnectionPrioritizationDTO } from '@api/fastAPI.schemas'
import { ErrorFieldBase } from '@ui/forms'
import { useSnackbar } from '@ui/snackbar'
import ShearConnectionPrioritizationList from '../ShearConnectionPrioritizationList'
import { SHEAR_CONNECTION_CATEGORIES } from '../constants'
import {
  emptyShearPrioritizationLabels,
  resolveShearPrioritizationChange,
  shearPrioritizationErrorMessage,
  shearPrioritizationWarningMessage,
  type ShearPrioritizationCategory,
} from './utils'

interface Props {
  connections: ShearConnectionDTO[]
  value: ShearConnectionPrioritizationDTO
  onChange: (next: ShearConnectionPrioritizationDTO) => void
}

/**
 * The twelve storey/placement/position category lists — a wall is anchored at
 * its foot and at its head, each with its own Schubverbinder. Shared by the
 * organisation library and the per-project one, which differ only in where
 * they save to.
 */
const ShearConnectionPrioritizationLists = ({
  connections,
  value,
  onChange,
}: Props): ReactElement => {
  const { enqueueSnackbar } = useSnackbar()
  /**
   * The latest edit, shown right away like the Zugverbinder lists do instead of
   * waiting for the save round trip and refetch. It stays in charge until the
   * server hands back a `value` that matches it (its save has landed) — not
   * merely a new one, since an earlier save's response would otherwise undo a
   * later edit. It also keeps the lists in a state that must not be saved on
   * screen — every category needs at least one Schubverbinder; removing the
   * last one still takes effect, it just isn't persisted until the category has
   * an anchor again.
   */
  const [draft, setDraft] = useState<ShearConnectionPrioritizationDTO | null>(null)
  const current = draft && !isEqual(draft, value) ? draft : value
  const missingLabels = useMemo(() => emptyShearPrioritizationLabels(current), [current])

  const handleChange = useCallback(
    (categoryId: ShearPrioritizationCategory, next: string[]) => {
      const change = resolveShearPrioritizationChange(current, categoryId, next)
      setDraft(change.prioritization)

      if (change.kind === 'incomplete') {
        enqueueSnackbar(shearPrioritizationErrorMessage(change.missingLabels), {
          variant: 'error',
        })
        return
      }

      onChange(change.prioritization)
    },
    [current, enqueueSnackbar, onChange],
  )

  return (
    <Stack spacing={3}>
      {missingLabels.length > 0 && (
        <ErrorFieldBase
          severity="error"
          message={shearPrioritizationWarningMessage(missingLabels)}
          data-cy="shear-connection-prioritization-warning"
        />
      )}

      {SHEAR_CONNECTION_CATEGORIES.map(({ id, label }) => (
        <ShearConnectionPrioritizationList
          key={id}
          title={label}
          value={current[id] ?? []}
          onChange={next => handleChange(id, next)}
          availableConnections={connections}
          data-cy={`shear-prioritization-${id}`}
        />
      ))}
    </Stack>
  )
}

export default ShearConnectionPrioritizationLists
